import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DocumentUploadPanel } from '../components/DocumentUploadPanel';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    AlertTriangle, 
    Plus, 
    Eye, 
    CheckCircle, 
    Clock, 
    Filter,
    XCircle,
    AlertCircle,
    ClipboardList,
    Mail,
    Calendar,
    Search,
    FileWarning,
    Activity,
    BarChart3,
    Bell,
    Trash2,
    FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const statusLabels = {
    aberta: { label: 'Aberta', class: 'bg-blue-600' },
    em_analise: { label: 'Em Análise', class: 'bg-yellow-600' },
    em_tratamento: { label: 'Em Tratamento', class: 'bg-orange-600' },
    aguardando_verificacao: { label: 'Aguard. Verificação', class: 'bg-purple-600' },
    encerrada: { label: 'Encerrada', class: 'bg-green-600' },
    cancelada: { label: 'Cancelada', class: 'bg-slate-600' }
};

const prioridadeLabels = {
    baixa: { label: 'Baixa', class: 'bg-green-600' },
    media: { label: 'Média', class: 'bg-yellow-600' },
    alta: { label: 'Alta', class: 'bg-orange-600' },
    critica: { label: 'Crítica', class: 'bg-red-600' }
};

const tipoLabels = {
    processo: 'Processo',
    produto: 'Produto',
    servico: 'Serviço',
    seguranca: 'Segurança',
    ambiental: 'Ambiental',
    regulatoria: 'Regulatória',
    documentacao: 'Documentação',
    manutencao: 'Manutenção',
    outros: 'Outros'
};

const origemLabels = {
    auditoria_interna: 'Auditoria Interna',
    auditoria_externa: 'Auditoria Externa',
    reclamacao_cliente: 'Reclamação de Cliente',
    inspecao: 'Inspeção',
    observacao_tripulacao: 'Observação da Tripulação',
    falha_equipamento: 'Falha de Equipamento',
    acidente: 'Acidente',
    outros: 'Outros'
};

const NaoConformidadesPage = () => {
    const [ncs, setNcs] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [alertas, setAlertas] = useState([]);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [convoys, setConvoys] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [acaoDialogOpen, setAcaoDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [verificacaoDialogOpen, setVerificacaoDialogOpen] = useState(false);
    const [selectedNc, setSelectedNc] = useState(null);
    const [activeTab, setActiveTab] = useState('lista');
    const { hasRole, user } = useAuth();
    const canDelete = hasRole(['admin']);

    const [filters, setFilters] = useState({
        status: '',
        prioridade: '',
        tipo: '',
        embarcacao_id: '',
        busca: '',
        data_inicio: '',
        data_fim: ''
    });

    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        tipo: '',
        origem: '',
        prioridade: 'media',
        embarcacao_tipo: '',
        embarcacao_id: '',
        data_ocorrencia: new Date().toISOString().split('T')[0],
        local_ocorrencia: '',
        causa_raiz: '',
        impacto: '',
        prazo_resolucao: '',
        responsavel_id: '',
        notificar_emails: ''
    });

    const [acaoData, setAcaoData] = useState({
        descricao: '',
        responsavel_id: '',
        prazo: '',
        observacoes: ''
    });

    const [statusData, setStatusData] = useState({
        status: '',
        observacao: ''
    });

    const [verificacaoData, setVerificacaoData] = useState({
        aprovado: true,
        observacao: ''
    });

    useEffect(() => {
        fetchBaseData();
    }, []);

    useEffect(() => {
        fetchNcs();
        fetchDashboard();
        fetchAlertas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const fetchBaseData = async () => {
        try {
            const [empRes, balsasRes, usersRes, convoysRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`),
                axios.get(`${API}/users`),
                axios.get(`${API}/convoys`)
            ]);
            setEmpurradores(empRes.data);
            setBalsas(balsasRes.data);
            setUsers(usersRes.data.filter(u => u.ativo));
            setConvoys(convoysRes.data);
        } catch (error) {
            console.error('Erro ao carregar dados base:', error);
        }
    };

    const fetchNcs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.prioridade) params.append('prioridade', filters.prioridade);
            if (filters.tipo) params.append('tipo', filters.tipo);
            if (filters.embarcacao_id) params.append('embarcacao_id', filters.embarcacao_id);
            if (filters.busca) params.append('busca', filters.busca);
            if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
            if (filters.data_fim) params.append('data_fim', filters.data_fim);

            const res = await axios.get(`${API}/nao-conformidades?${params.toString()}`);
            setNcs(res.data);
        } catch (error) {
            toast.error('Erro ao carregar não conformidades');
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboard = async () => {
        try {
            const res = await axios.get(`${API}/nao-conformidades/dashboard`);
            setDashboard(res.data);
        } catch (error) {
            console.error('Erro ao carregar dashboard:', error);
        }
    };

    const fetchAlertas = async () => {
        try {
            const res = await axios.get(`${API}/nao-conformidades/alertas?dias=14`);
            setAlertas(res.data);
        } catch (error) {
            console.error('Erro ao carregar alertas:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                notificar_emails: formData.notificar_emails 
                    ? formData.notificar_emails.split(',').map(e => e.trim()).filter(e => e)
                    : []
            };
            
            // Buscar nome do responsável
            if (payload.responsavel_id) {
                const responsavel = users.find(u => u.id === payload.responsavel_id);
                payload.responsavel_nome = responsavel?.nome || '';
            }

            await axios.post(`${API}/nao-conformidades`, payload);
            toast.success('Não conformidade criada com sucesso');
            setDialogOpen(false);
            resetForm();
            fetchNcs();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar não conformidade');
        }
    };

    const handleAddAcao = async (e) => {
        e.preventDefault();
        try {
            const responsavel = users.find(u => u.id === acaoData.responsavel_id);
            const payload = {
                ...acaoData,
                responsavel_nome: responsavel?.nome || user.nome
            };
            
            await axios.post(`${API}/nao-conformidades/${selectedNc.id}/acoes`, payload);
            toast.success('Ação corretiva adicionada');
            setAcaoDialogOpen(false);
            setAcaoData({ descricao: '', responsavel_id: '', prazo: '', observacoes: '' });
            fetchNcDetails(selectedNc.id);
        } catch (error) {
            toast.error('Erro ao adicionar ação corretiva');
        }
    };

    const handleUpdateAcao = async (acaoId, newStatus) => {
        try {
            await axios.put(`${API}/nao-conformidades/${selectedNc.id}/acoes/${acaoId}`, {
                status: newStatus
            });
            toast.success('Ação atualizada');
            fetchNcDetails(selectedNc.id);
        } catch (error) {
            toast.error('Erro ao atualizar ação');
        }
    };

    const handleChangeStatus = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API}/nao-conformidades/${selectedNc.id}/status`, statusData);
            toast.success('Status atualizado');
            setStatusDialogOpen(false);
            setStatusData({ status: '', observacao: '' });
            fetchNcs();
            fetchDashboard();
            fetchNcDetails(selectedNc.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
        }
    };

    const handleVerificar = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/nao-conformidades/${selectedNc.id}/verificar`, verificacaoData);
            toast.success(verificacaoData.aprovado ? 'NC encerrada com sucesso' : 'NC devolvida para tratamento');
            setVerificacaoDialogOpen(false);
            setVerificacaoData({ aprovado: true, observacao: '' });
            fetchNcs();
            fetchDashboard();
            fetchNcDetails(selectedNc.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao verificar NC');
        }
    };

    const handleEnviarAlertas = async () => {
        try {
            const res = await axios.post(`${API}/nao-conformidades/enviar-alertas-prazo?dias=7`);
            toast.success(res.data.message);
        } catch (error) {
            toast.error('Erro ao enviar alertas');
        }
    };

    const handleDeleteNc = async (ncId, ncNumber) => {
        if (!window.confirm(`Tem certeza que deseja EXCLUIR a não conformidade ${ncNumber}?\n\nEsta ação não pode ser desfeita.`)) return;
        
        try {
            await axios.delete(`${API}/nao-conformidades/${ncId}`);
            toast.success('Não conformidade excluída com sucesso');
            fetchNcs();
            fetchDashboard();
            setDetailDialogOpen(false);
        } catch (error) {
            toast.error('Erro ao excluir não conformidade');
        }
    };

    const fetchNcDetails = async (ncId) => {
        try {
            const res = await axios.get(`${API}/nao-conformidades/${ncId}`);
            setSelectedNc(res.data);
        } catch (error) {
            toast.error('Erro ao carregar detalhes');
        }
    };

    const openDetails = async (nc) => {
        await fetchNcDetails(nc.id);
        setDetailDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({
            titulo: '',
            descricao: '',
            tipo: '',
            origem: '',
            prioridade: 'media',
            embarcacao_tipo: '',
            embarcacao_id: '',
            data_ocorrencia: new Date().toISOString().split('T')[0],
            local_ocorrencia: '',
            causa_raiz: '',
            impacto: '',
            prazo_resolucao: '',
            responsavel_id: '',
            notificar_emails: ''
        });
    };

    const getEmbarcacoes = () => {
        if (formData.embarcacao_tipo === 'empurrador') return empurradores;
        if (formData.embarcacao_tipo === 'balsa') return balsas;
        return [];
    };

    const getEmbarcacaoNome = (tipo, id) => {
        if (!id) return null;
        if (tipo === 'empurrador') {
            return empurradores.find(e => e.id === id)?.nome;
        }
        if (tipo === 'balsa') {
            return balsas.find(b => b.id === id)?.nome;
        }
        // Buscar em convoys também
        const convoy = convoys.find(c => c.id === id);
        if (convoy) return convoy.nome;
        return null;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                        <FileWarning className="w-6 h-6 text-red-400" />
                        Gestão de Não Conformidades
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Registro, acompanhamento e tratamento de não conformidades
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        onClick={async () => {
                            try {
                                toast.info('Gerando relatório PDF...');
                                const params = new URLSearchParams();
                                if (filters.status) params.append('status', filters.status);
                                if (filters.prioridade) params.append('prioridade', filters.prioridade);
                                if (filters.tipo) params.append('tipo', filters.tipo);
                                if (filters.embarcacao_id) params.append('embarcacao_id', filters.embarcacao_id);
                                if (filters.busca) params.append('busca', filters.busca);
                                if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
                                if (filters.data_fim) params.append('data_fim', filters.data_fim);
                                const res = await axios.get(`${API}/nao-conformidades/relatorio/pdf?${params.toString()}`, { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `relatorio_nao_conformidades_${new Date().toISOString().slice(0, 10)}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success('Relatório PDF gerado');
                            } catch {
                                toast.error('Erro ao gerar relatório PDF');
                            }
                        }}
                        className="border-slate-700 text-slate-300"
                        data-testid="btn-relatorio-pdf-nc"
                    >
                        <FileDown className="w-4 h-4 mr-2" />
                        Relatório PDF
                    </Button>
                    {hasRole(['admin', 'gestor']) && (
                        <Button 
                            variant="outline" 
                            onClick={handleEnviarAlertas}
                            className="border-slate-700 text-slate-300"
                            data-testid="btn-enviar-alertas"
                        >
                            <Bell className="w-4 h-4 mr-2" />
                            Enviar Alertas
                        </Button>
                    )}
                    {hasRole(['admin', 'gestor', 'operador']) && (
                        <Button 
                            onClick={() => setDialogOpen(true)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="btn-nova-nc"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nova NC
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-800 pb-2">
                <Button 
                    variant={activeTab === 'lista' ? 'default' : 'ghost'}
                    onClick={() => setActiveTab('lista')}
                    className={activeTab === 'lista' ? 'bg-cyan-600' : 'text-slate-400'}
                    data-testid="tab-lista"
                >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Lista
                </Button>
                <Button 
                    variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
                    onClick={() => setActiveTab('dashboard')}
                    className={activeTab === 'dashboard' ? 'bg-cyan-600' : 'text-slate-400'}
                    data-testid="tab-dashboard"
                >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Dashboard
                </Button>
                <Button 
                    variant={activeTab === 'alertas' ? 'default' : 'ghost'}
                    onClick={() => setActiveTab('alertas')}
                    className={activeTab === 'alertas' ? 'bg-cyan-600' : 'text-slate-400'}
                    data-testid="tab-alertas"
                >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Alertas
                    {alertas.length > 0 && (
                        <span className="ml-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                            {alertas.length}
                        </span>
                    )}
                </Button>
            </div>

            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && dashboard && (
                <div className="space-y-6">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-white">{dashboard.total}</p>
                                <p className="text-slate-400 text-sm">Total</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-blue-400">{dashboard.abertas}</p>
                                <p className="text-slate-400 text-sm">Abertas</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-green-400">{dashboard.encerradas}</p>
                                <p className="text-slate-400 text-sm">Encerradas</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-red-400">{dashboard.atrasadas}</p>
                                <p className="text-slate-400 text-sm">Atrasadas</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-yellow-400">{dashboard.proximas_vencer}</p>
                                <p className="text-slate-400 text-sm">Próx. Vencer</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-slate-400">{dashboard.canceladas}</p>
                                <p className="text-slate-400 text-sm">Canceladas</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Por Status */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-slate-400">Por Status</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {Object.entries(dashboard.por_status || {}).map(([status, count]) => (
                                    <div key={status} className="flex justify-between items-center">
                                        <span className={`px-2 py-1 rounded text-xs text-white ${statusLabels[status]?.class || 'bg-slate-600'}`}>
                                            {statusLabels[status]?.label || status}
                                        </span>
                                        <span className="text-white font-bold">{count}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Por Prioridade */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-slate-400">Por Prioridade</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {Object.entries(dashboard.por_prioridade || {}).map(([prioridade, count]) => (
                                    <div key={prioridade} className="flex justify-between items-center">
                                        <span className={`px-2 py-1 rounded text-xs text-white ${prioridadeLabels[prioridade]?.class || 'bg-slate-600'}`}>
                                            {prioridadeLabels[prioridade]?.label || prioridade}
                                        </span>
                                        <span className="text-white font-bold">{count}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Por Tipo */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-slate-400">Por Tipo</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 max-h-48 overflow-y-auto">
                                {Object.entries(dashboard.por_tipo || {}).map(([tipo, count]) => (
                                    <div key={tipo} className="flex justify-between items-center">
                                        <span className="text-slate-300 text-sm">{tipoLabels[tipo] || tipo}</span>
                                        <span className="text-white font-bold">{count}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Por Origem */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-slate-400">Por Origem</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 max-h-48 overflow-y-auto">
                                {Object.entries(dashboard.por_origem || {}).map(([origem, count]) => (
                                    <div key={origem} className="flex justify-between items-center">
                                        <span className="text-slate-300 text-sm truncate" title={origemLabels[origem] || origem}>
                                            {origemLabels[origem] || origem}
                                        </span>
                                        <span className="text-white font-bold">{count}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {/* Alertas Tab */}
            {activeTab === 'alertas' && (
                <div className="space-y-4">
                    {alertas.length === 0 ? (
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-8 text-center">
                                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                                <p className="text-slate-400">Nenhum alerta de prazo no momento</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {alertas.map((nc) => (
                                <Card 
                                    key={nc.id} 
                                    className={`bg-slate-900 border-l-4 ${
                                        nc.status_alerta === 'vencida' ? 'border-red-500' :
                                        nc.status_alerta === 'critico' ? 'border-orange-500' : 'border-yellow-500'
                                    }`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-cyan-400 font-mono text-sm">{nc.numero_nc}</span>
                                                    <span className={`px-2 py-0.5 rounded text-xs text-white ${prioridadeLabels[nc.prioridade]?.class}`}>
                                                        {prioridadeLabels[nc.prioridade]?.label}
                                                    </span>
                                                </div>
                                                <h3 className="text-white font-medium">{nc.titulo}</h3>
                                                <p className="text-slate-400 text-sm mt-1">
                                                    Prazo: {nc.prazo_resolucao} | {nc.dias_restantes < 0 ? `${Math.abs(nc.dias_restantes)} dias atrasado` : `${nc.dias_restantes} dias restantes`}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-3 py-1 rounded text-sm font-bold ${
                                                    nc.dias_restantes < 0 ? 'bg-red-600 text-white' :
                                                    nc.dias_restantes <= 3 ? 'bg-orange-600 text-white' : 'bg-yellow-600 text-white'
                                                }`}>
                                                    {nc.dias_restantes < 0 ? 'VENCIDA' : nc.dias_restantes <= 3 ? 'URGENTE' : 'ALERTA'}
                                                </span>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => openDetails(nc)}
                                                    className="border-slate-700"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Lista Tab */}
            {activeTab === 'lista' && (
                <>
                    {/* Filters */}
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                                <div className="col-span-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input 
                                            placeholder="Buscar por título, número ou descrição..."
                                            value={filters.busca}
                                            onChange={(e) => setFilters(f => ({ ...f, busca: e.target.value }))}
                                            className="pl-10 bg-slate-800 border-slate-700 text-white"
                                        />
                                    </div>
                                </div>
                                <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        {Object.entries(statusLabels).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.prioridade || "all"} onValueChange={(v) => setFilters(f => ({ ...f, prioridade: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Prioridade" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {Object.entries(prioridadeLabels).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.embarcacao_id || "all"} onValueChange={(v) => setFilters(f => ({ ...f, embarcacao_id: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Embarcação" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas Embarcações</SelectItem>
                                        {empurradores.length > 0 && (
                                            <>
                                                <SelectItem value="header-emp" disabled className="text-slate-500 font-semibold">-- Empurradores --</SelectItem>
                                                {empurradores.map((e) => (
                                                    <SelectItem key={`emp-${e.id}`} value={e.id}>{e.nome}</SelectItem>
                                                ))}
                                            </>
                                        )}
                                        {balsas.length > 0 && (
                                            <>
                                                <SelectItem value="header-bal" disabled className="text-slate-500 font-semibold">-- Balsas --</SelectItem>
                                                {balsas.map((b) => (
                                                    <SelectItem key={`bal-${b.id}`} value={b.id}>{b.nome}</SelectItem>
                                                ))}
                                            </>
                                        )}
                                        {convoys.length > 0 && (
                                            <>
                                                <SelectItem value="header-conv" disabled className="text-slate-500 font-semibold">-- Outras Embarcações --</SelectItem>
                                                {convoys.map((c) => (
                                                    <SelectItem key={`conv-${c.id}`} value={c.id}>{c.nome}</SelectItem>
                                                ))}
                                            </>
                                        )}
                                    </SelectContent>
                                </Select>
                                <Input 
                                    type="date"
                                    value={filters.data_inicio}
                                    onChange={(e) => setFilters(f => ({ ...f, data_inicio: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    title="Data início"
                                />
                                <Input 
                                    type="date"
                                    value={filters.data_fim}
                                    onChange={(e) => setFilters(f => ({ ...f, data_fim: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    title="Data fim"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Table */}
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-slate-800 hover:bg-slate-800/50">
                                            <TableHead className="text-slate-400">Número</TableHead>
                                            <TableHead className="text-slate-400">Título</TableHead>
                                            <TableHead className="text-slate-400">Embarcação</TableHead>
                                            <TableHead className="text-slate-400">Tipo</TableHead>
                                            <TableHead className="text-slate-400">Prioridade</TableHead>
                                            <TableHead className="text-slate-400">Status</TableHead>
                                            <TableHead className="text-slate-400">Data</TableHead>
                                            <TableHead className="text-slate-400">Prazo</TableHead>
                                            <TableHead className="text-slate-400 text-right">Ações</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={9} className="text-center text-slate-400 py-8">
                                                    Carregando...
                                                </TableCell>
                                            </TableRow>
                                        ) : ncs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={9} className="text-center text-slate-400 py-8">
                                                    Nenhuma não conformidade encontrada
                                                </TableCell>
                                            </TableRow>
                                        ) : ncs.map((nc) => (
                                            <TableRow key={nc.id} className="border-slate-800 hover:bg-slate-800/50">
                                                <TableCell className="font-mono text-cyan-400">{nc.numero_nc}</TableCell>
                                                <TableCell className="text-white max-w-xs truncate" title={nc.titulo}>
                                                    {nc.titulo}
                                                </TableCell>
                                                <TableCell className="text-slate-300">
                                                    {getEmbarcacaoNome(nc.embarcacao_tipo, nc.embarcacao_id) || '-'}
                                                </TableCell>
                                                <TableCell className="text-slate-300">{tipoLabels[nc.tipo] || nc.tipo}</TableCell>
                                                <TableCell>
                                                    <span className={`px-2 py-1 rounded text-xs text-white ${prioridadeLabels[nc.prioridade]?.class || 'bg-slate-600'}`}>
                                                        {prioridadeLabels[nc.prioridade]?.label || nc.prioridade}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`px-2 py-1 rounded text-xs text-white ${statusLabels[nc.status]?.class || 'bg-slate-600'}`}>
                                                        {statusLabels[nc.status]?.label || nc.status}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-slate-300">{nc.data_ocorrencia}</TableCell>
                                                <TableCell className="text-slate-300">{nc.prazo_resolucao || '-'}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex gap-1 justify-end">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            onClick={() => openDetails(nc)}
                                                            data-testid={`btn-ver-nc-${nc.id}`}
                                                            title="Ver detalhes"
                                                        >
                                                            <Eye className="w-4 h-4 text-slate-400" />
                                                        </Button>
                                                        {canDelete && (
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm" 
                                                                onClick={() => handleDeleteNc(nc.id, nc.nc_number)}
                                                                className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                                                                data-testid={`btn-delete-nc-${nc.id}`}
                                                                title="Excluir NC"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Dialog: Nova NC */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-400" />
                            Nova Não Conformidade
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <Label className="text-slate-300">Título *</Label>
                                <Input 
                                    value={formData.titulo}
                                    onChange={(e) => setFormData(f => ({ ...f, titulo: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Tipo *</Label>
                                <Select value={formData.tipo || undefined} onValueChange={(v) => setFormData(f => ({ ...f, tipo: v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(tipoLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Origem *</Label>
                                <Select value={formData.origem || undefined} onValueChange={(v) => setFormData(f => ({ ...f, origem: v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(origemLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Prioridade *</Label>
                                <Select value={formData.prioridade || undefined} onValueChange={(v) => setFormData(f => ({ ...f, prioridade: v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(prioridadeLabels).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Data da Ocorrência *</Label>
                                <Input 
                                    type="date"
                                    value={formData.data_ocorrencia}
                                    onChange={(e) => setFormData(f => ({ ...f, data_ocorrencia: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Tipo de Embarcação</Label>
                                <Select 
                                    value={formData.embarcacao_tipo || "none"} 
                                    onValueChange={(v) => setFormData(f => ({ ...f, embarcacao_tipo: v === "none" ? "" : v, embarcacao_id: '' }))}
                                >
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Selecione (opcional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Nenhuma</SelectItem>
                                        <SelectItem value="empurrador">Empurrador</SelectItem>
                                        <SelectItem value="balsa">Balsa</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Embarcação</Label>
                                <Select 
                                    value={formData.embarcacao_id || "none"} 
                                    onValueChange={(v) => setFormData(f => ({ ...f, embarcacao_id: v === "none" ? "" : v }))}
                                    disabled={!formData.embarcacao_tipo || formData.embarcacao_tipo === "none"}
                                >
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Selecione...</SelectItem>
                                        {getEmbarcacoes().map((emb) => (
                                            <SelectItem key={emb.id} value={emb.id}>{emb.nome}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Local da Ocorrência</Label>
                                <Input 
                                    value={formData.local_ocorrencia}
                                    onChange={(e) => setFormData(f => ({ ...f, local_ocorrencia: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    placeholder="Ex: Porto de Santos"
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Prazo de Resolução</Label>
                                <Input 
                                    type="date"
                                    value={formData.prazo_resolucao}
                                    onChange={(e) => setFormData(f => ({ ...f, prazo_resolucao: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Responsável</Label>
                                <Select 
                                    value={formData.responsavel_id || "none"} 
                                    onValueChange={(v) => setFormData(f => ({ ...f, responsavel_id: v === "none" ? "" : v }))}
                                >
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Selecione...</SelectItem>
                                        {users.filter(u => u.ativo).map((u) => (
                                            <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-slate-300">Descrição *</Label>
                                <Textarea 
                                    value={formData.descricao}
                                    onChange={(e) => setFormData(f => ({ ...f, descricao: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    rows={3}
                                    required
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-slate-300">Impacto</Label>
                                <Textarea 
                                    value={formData.impacto}
                                    onChange={(e) => setFormData(f => ({ ...f, impacto: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    rows={2}
                                    placeholder="Descreva o impacto da não conformidade..."
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-slate-300">Emails para Notificar (separados por vírgula)</Label>
                                <Input 
                                    value={formData.notificar_emails}
                                    onChange={(e) => setFormData(f => ({ ...f, notificar_emails: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    placeholder="email1@exemplo.com, email2@exemplo.com"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-red-600 hover:bg-red-700">
                                Registrar NC
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Detalhes NC */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <FileWarning className="w-5 h-5 text-red-400" />
                            {selectedNc?.numero_nc} - {selectedNc?.titulo}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedNc && (
                        <div className="space-y-6">
                            {/* Status e Prioridade */}
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className={`px-3 py-1 rounded text-sm text-white ${statusLabels[selectedNc.status]?.class}`}>
                                    {statusLabels[selectedNc.status]?.label}
                                </span>
                                <span className={`px-3 py-1 rounded text-sm text-white ${prioridadeLabels[selectedNc.prioridade]?.class}`}>
                                    Prioridade: {prioridadeLabels[selectedNc.prioridade]?.label}
                                </span>
                                {hasRole(['admin', 'gestor', 'operador']) && selectedNc.status !== 'encerrada' && selectedNc.status !== 'cancelada' && (
                                    <>
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={() => { setStatusData({ status: '', observacao: '' }); setStatusDialogOpen(true); }}
                                            className="border-slate-700 text-slate-300"
                                        >
                                            <Activity className="w-4 h-4 mr-1" />
                                            Alterar Status
                                        </Button>
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={() => { setAcaoData({ descricao: '', responsavel_id: '', prazo: '', observacoes: '' }); setAcaoDialogOpen(true); }}
                                            className="border-slate-700 text-slate-300"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Ação Corretiva
                                        </Button>
                                    </>
                                )}
                                {hasRole(['admin', 'gestor']) && selectedNc.status === 'aguardando_verificacao' && (
                                    <Button 
                                        size="sm" 
                                        onClick={() => { setVerificacaoData({ aprovado: true, observacao: '' }); setVerificacaoDialogOpen(true); }}
                                        className="bg-green-600 hover:bg-green-700"
                                    >
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        Verificar
                                    </Button>
                                )}
                            </div>

                            {/* Info Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-800/50 p-4 rounded">
                                <div>
                                    <p className="text-slate-400 text-xs">Tipo</p>
                                    <p className="text-white">{tipoLabels[selectedNc.tipo] || selectedNc.tipo}</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Origem</p>
                                    <p className="text-white">{origemLabels[selectedNc.origem] || selectedNc.origem}</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Data Ocorrência</p>
                                    <p className="text-white">{selectedNc.data_ocorrencia}</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Prazo</p>
                                    <p className="text-white">{selectedNc.prazo_resolucao || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Criado por</p>
                                    <p className="text-white">{selectedNc.criado_por_nome}</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Responsável</p>
                                    <p className="text-white">{selectedNc.responsavel_nome || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Local</p>
                                    <p className="text-white">{selectedNc.local_ocorrencia || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Embarcação</p>
                                    <p className="text-white">
                                        {selectedNc.embarcacao_id 
                                            ? getEmbarcacaoNome(selectedNc.embarcacao_tipo, selectedNc.embarcacao_id)
                                            : '-'}
                                    </p>
                                </div>
                            </div>

                            {/* Descrição */}
                            <div>
                                <h4 className="text-slate-400 text-sm mb-2">Descrição</h4>
                                <p className="text-white bg-slate-800/50 p-3 rounded">{selectedNc.descricao}</p>
                            </div>

                            {/* Impacto */}
                            {selectedNc.impacto && (
                                <div>
                                    <h4 className="text-slate-400 text-sm mb-2">Impacto</h4>
                                    <p className="text-white bg-slate-800/50 p-3 rounded">{selectedNc.impacto}</p>
                                </div>
                            )}

                            {/* Causa Raiz */}
                            {selectedNc.causa_raiz && (
                                <div>
                                    <h4 className="text-slate-400 text-sm mb-2">Causa Raiz</h4>
                                    <p className="text-white bg-slate-800/50 p-3 rounded">{selectedNc.causa_raiz}</p>
                                </div>
                            )}

                            {/* Ações Corretivas */}
                            <div>
                                <h4 className="text-slate-400 text-sm mb-2">Ações Corretivas ({selectedNc.acoes_corretivas?.length || 0})</h4>
                                {selectedNc.acoes_corretivas?.length > 0 ? (
                                    <div className="space-y-2">
                                        {selectedNc.acoes_corretivas.map((acao, idx) => (
                                            <div key={acao.id} className="bg-slate-800/50 p-3 rounded border-l-4 border-cyan-500">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <p className="text-white">{acao.descricao}</p>
                                                        <p className="text-slate-400 text-xs mt-1">
                                                            Responsável: {acao.responsavel_nome} | Prazo: {acao.prazo}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 rounded text-xs ${
                                                            acao.status === 'concluida' ? 'bg-green-600' :
                                                            acao.status === 'em_andamento' ? 'bg-yellow-600' :
                                                            acao.status === 'atrasada' ? 'bg-red-600' : 'bg-slate-600'
                                                        } text-white`}>
                                                            {acao.status === 'pendente' ? 'Pendente' :
                                                             acao.status === 'em_andamento' ? 'Em Andamento' :
                                                             acao.status === 'concluida' ? 'Concluída' : 'Atrasada'}
                                                        </span>
                                                        {hasRole(['admin', 'gestor', 'operador']) && acao.status !== 'concluida' && (
                                                            <Button 
                                                                size="sm" 
                                                                variant="ghost"
                                                                onClick={() => handleUpdateAcao(acao.id, acao.status === 'pendente' ? 'em_andamento' : 'concluida')}
                                                            >
                                                                <CheckCircle className="w-4 h-4 text-green-400" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-slate-500 text-sm">Nenhuma ação corretiva registrada</p>
                                )}
                            </div>

                            {/* Histórico */}
                            <div>
                                <h4 className="text-slate-400 text-sm mb-2">Histórico</h4>
                                <div className="bg-slate-800/50 p-3 rounded max-h-48 overflow-y-auto space-y-2">
                                    {selectedNc.historico?.slice().reverse().map((h, idx) => (
                                        <div key={idx} className="text-sm border-b border-slate-700 pb-2 last:border-0">
                                            <div className="flex justify-between">
                                                <span className="text-cyan-400">{h.acao}</span>
                                                <span className="text-slate-500 text-xs">{new Date(h.data).toLocaleString('pt-BR')}</span>
                                            </div>
                                            <p className="text-slate-300">{h.usuario}</p>
                                            {h.detalhe && <p className="text-slate-400 text-xs">{h.detalhe}</p>}
                                            {h.status_anterior && (
                                                <p className="text-slate-400 text-xs">
                                                    Status: {statusLabels[h.status_anterior]?.label} → {statusLabels[h.status_novo]?.label}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Documentos/Evidencias Upload */}
                    {selectedNc && (
                        <DocumentUploadPanel modulo="nao_conformidades" entidadeId={selectedNc.id} entidadeNome={selectedNc.numero_nc} />
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog: Alterar Status */}
            <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Alterar Status</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleChangeStatus} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Novo Status *</Label>
                            <Select value={statusData.status || undefined} onValueChange={(v) => setStatusData(s => ({ ...s, status: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(statusLabels).map(([key, val]) => (
                                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-300">Observação</Label>
                            <Textarea 
                                value={statusData.observacao}
                                onChange={(e) => setStatusData(s => ({ ...s, observacao: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                rows={3}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={!statusData.status}>
                                Alterar Status
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Adicionar Ação Corretiva */}
            <Dialog open={acaoDialogOpen} onOpenChange={setAcaoDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Adicionar Ação Corretiva</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddAcao} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Descrição *</Label>
                            <Textarea 
                                value={acaoData.descricao}
                                onChange={(e) => setAcaoData(a => ({ ...a, descricao: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                rows={3}
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-slate-300">Responsável</Label>
                            <Select value={acaoData.responsavel_id || undefined} onValueChange={(v) => setAcaoData(a => ({ ...a, responsavel_id: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-300">Prazo *</Label>
                            <Input 
                                type="date"
                                value={acaoData.prazo}
                                onChange={(e) => setAcaoData(a => ({ ...a, prazo: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-slate-300">Observações</Label>
                            <Textarea 
                                value={acaoData.observacoes}
                                onChange={(e) => setAcaoData(a => ({ ...a, observacoes: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                rows={2}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setAcaoDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                                Adicionar
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Verificação */}
            <Dialog open={verificacaoDialogOpen} onOpenChange={setVerificacaoDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Verificar Não Conformidade</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleVerificar} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Resultado da Verificação</Label>
                            <div className="flex gap-4 mt-2">
                                <Button 
                                    type="button"
                                    variant={verificacaoData.aprovado ? "default" : "outline"}
                                    onClick={() => setVerificacaoData(v => ({ ...v, aprovado: true }))}
                                    className={verificacaoData.aprovado ? "bg-green-600" : "border-slate-700"}
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Aprovar e Encerrar
                                </Button>
                                <Button 
                                    type="button"
                                    variant={!verificacaoData.aprovado ? "default" : "outline"}
                                    onClick={() => setVerificacaoData(v => ({ ...v, aprovado: false }))}
                                    className={!verificacaoData.aprovado ? "bg-red-600" : "border-slate-700"}
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reprovar
                                </Button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-slate-300">Observação</Label>
                            <Textarea 
                                value={verificacaoData.observacao}
                                onChange={(e) => setVerificacaoData(v => ({ ...v, observacao: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                rows={3}
                                placeholder={verificacaoData.aprovado ? "Observações finais..." : "Motivo da reprovação..."}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setVerificacaoDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className={verificacaoData.aprovado ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}>
                                Confirmar
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NaoConformidadesPage;
